<template>
  <div id="app" class="stripe-checkout-container">
    <Bandeau
      v-if="payment"
      :show-rgpd.sync="showRgpd"
      :type="form.type"
      :description="form.description"
    />

    <div class="stripe-checkout-wrapper">
      <header class="stripe-checkout-header" v-if="step !== 2">
        <h1 @click="reset" class="stripe-checkout-title">Résiliation {{ form.description }} en ligne</h1>
        <p class="stripe-checkout-price">Service complet — <strong>39,90 € TTC</strong></p>
        <div class="stripe-checkout-trust">
          <span class="trust-item">Paiement sécurisé</span>
          <span class="trust-separator">·</span>
          <span class="trust-item">Envoi sous 24h</span>
          <span class="trust-separator">·</span>
          <span class="trust-item">93% de satisfaction</span>
        </div>
        <p class="stripe-checkout-social-proof">{{ dailyCount }} résiliations effectuées aujourd'hui</p>
      </header>

      <main class="stripe-checkout-main">
        <div class="stripe-checkout-card" id="form">
          <span v-if="step === 1">
            <FormPaiement
              :loading="loading"
              :nom.sync="form.nom"
              :prenom.sync="form.prenom"
              :adresse.sync="form.adresse"
              :adresse2.sync="form.adresse2"
              :codePostal.sync="form.codePostal"
              :ville.sync="form.ville"
              :pays.sync="form.pays"
              :email.sync="form.email"
              :optin.sync="form.optin"
              :phone="form.phone"
              :contract="form.contract"
              :reference="'référence ' + form.description"
              :type="form.type"
              @valide="handlePayment"
            />
          </span>

          <Confirmation
            v-else-if="step === 2"
            :email="form.email"
          />

          <template v-else>
            <FormMobile
              v-if="form.type === 'mobile' || form.type === 'internet'"
              title="Numéro d'abonné / contrat sur votre facture"
              :loading="loading"
              :contact="contact"
              :phone.sync="form.phone"
              :contract.sync="form.contract"
              :type.sync="form.type"
              :services.sync="form.services"
              :description="form.description"
              :reference="'identifiant ' + form.description"
              :internet="internet"
              :effectiveDate.sync="form.effectiveDate"
              @valide="preparePayment"
            />

            <FormCanal
              v-else-if="form.type === 'canalsat' || form.type === 'canalplay' || form.type === 'canalfoot' || form.type === 'canal'"
              title="Numéro d'abonné dans votre espace client"
              :loading="loading"
              :contact="contact"
              :contract.sync="form.contract"
              :type.sync="form.type"
              :description="form.description"
              :reference="'identifiant ' + form.description"
              @valide="preparePayment"
            />

            <FormMutuelle
              v-else-if="form.type === 'mutuelle'"
              title="Numéro du contrat à résilier"
              :loading="loading"
              :contact="contact"
              :contract.sync="form.contract"
              :motif.sync="form.motif"
              :informations.sync="form.informations"
              :attachment.sync="form.attachment"
              :type.sync="form.type"
              :description="form.description"
              :reference="'numéro de contrat ' + form.description"
              @valide="preparePayment"
            />

            <FormSport
              v-else-if="form.type === 'sport'"
              :loading="loading"
              :contact="contact"
              :contract.sync="form.contract"
              :informations.sync="form.informations"
              :attachment.sync="form.attachment"
              :type.sync="form.type"
              :description="form.description"
              :reference="'numéro de contrat ' + form.description"
              @valide="preparePayment"
            />
          </template>
        </div>

        <EtapesCanal
          v-if="form.type === 'canalsat' || form.type === 'canalplay' || form.type === 'canalfoot' || form.type === 'canal'"
          class="stripe-checkout-steps"
        />
        <EtapesCommande v-else-if="step !== 2" class="stripe-checkout-steps" />
      </main>

      <footer class="stripe-checkout-footer">
        <button type="button" class="stripe-checkout-link" @click="showCgu = true">
          Mentions légales
          <span v-if="payment">et conditions générales de vente</span>
        </button>
        <span class="stripe-checkout-separator">•</span>
        <button type="button" class="stripe-checkout-link" @click="contactUs">
          Contact
        </button>
      </footer>
    </div>

    <el-drawer
      title="Mentions légales et conditions"
      :visible.sync="showCgu"
      direction="btt"
    >
      <Cgu :contact="contact" :payment="payment" />
    </el-drawer>

    <el-drawer
      title="POLITIQUE DE CONFIDENTIALITÉ / RGPD"
      :visible.sync="showRgpd"
      direction="ttb"
    >
      <Rgpd />
    </el-drawer>

    <el-dialog
      title="Votre demande est prise en compte"
      :visible.sync="showSuccessModal"
      width="500px"
      custom-class="success-modal"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="true"
    >
      <div class="success-modal__body">
        <p class="success-modal__text">
          La résiliation prendra effet dans un délai maximal de dix jours à compter de la date de prise en compte de votre demande par le service de résiliation.
        </p>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showSuccessModal = false" style="width: 100%;" class="success-modal-close-btn">
          Fermer
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Vue from 'vue'
import { handleCardPayment } from 'vue-stripe-elements-plus'
import { loadStripe } from '@stripe/stripe-js'
import Bandeau from './components/bandeau'
import Cgu from './components/cgu'
import Rgpd from './components/rgpd'
import EtapesCanal from './components/etapes-canal'
import EtapesCommande from './components/etapes-commande'
import FormMobile from './components/form/mobile'
import FormCanal from './components/form/canal'
import FormMutuelle from './components/form/mutuelle'
import FormSport from './components/form/sport'
import FormPaiement from './components/form/paiement'
import Confirmation from './components/confirmation'

export default {
  name: 'App',
  components: {
    Bandeau,
    Cgu,
    Rgpd,
    EtapesCanal,
    EtapesCommande,
    FormMobile,
    FormCanal,
    FormMutuelle,
    FormSport,
    FormPaiement,
    Confirmation
  },
  data () {
    let internet = true
    let description = 'Free'
    let services = ''
    let motif = ''
    let hostname = window.location.hostname + window.location.pathname
    let type = hostname.match(/mobile/) ? 'mobile' : 'internet'
    let product = type === 'mobile' ? 'FREE_MOBILE' : 'FREE'

    if (hostname.match(/orange/)) {
      product = 'ORANGE'
      description = 'Orange'
      services = (type === 'mobile' ? 'Téléphonie mobile' : 'Internet')
    } else if (hostname.match(/sosh/)) {
      description = 'Sosh'
    } else if (hostname.match(/redbysfr/)) {
      description = 'Red by SFR'
    } else if (hostname.match(/sfr/)) {
      product = 'SFR'
      description = 'SFR'
      services = (type === 'mobile' ? 'Téléphonie mobile' : 'Internet')
    } else if (hostname.match(/bouygues/)) {
      product = 'BOUYGUES_TELECOM'
      description = 'Bouygues Telecom'
      services = (type === 'mobile' ? 'Téléphonie mobile' : 'Internet')
    } else if (hostname.match(/bandyou/)) {
      description = 'B and You'
    } else if (hostname.match(/laposte/)) {
      description = 'La Poste Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/cic/)) {
      description = 'CIC Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/creditmutuel/)) {
      description = 'Credit Mutuel Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/coriolis/)) {
      description = 'Coriolis Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/clubbudget/)) {
      description = 'Club Budget Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/nrjmobile/)) {
      description = 'NRJ Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/symamobile/)) {
      description = 'Syma Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/auchantelecom/)) {
      description = 'Auchan Telecom'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/numericablemobile/)) {
      description = 'Numericable Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/m6mobile/)) {
      description = 'M6 Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/prixtel/)) {
      description = 'Prixtel'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/simyomobile/)) {
      description = 'Simyo Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/virginmobile/)) {
      description = 'Virgin Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/wibox/)) {
      description = 'Wibox'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/leclercmobile/)) {
      description = 'Leclerc Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/transatel/)) {
      description = 'Transatel'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/alicemobile/)) {
      description = 'Alice Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/breizhmobile/)) {
      description = 'Breizh Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/cdiscountmobile/)) {
      description = 'CDiscount Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/lebaramobile/)) {
      description = 'Lebara Mobile'
      type = 'mobile'
      internet = false
    } else if (hostname.match(/alsatistelecom/)) {
      description = 'Alsatis Telecom'
      type = 'internet'
      internet = false
    } else if (hostname.match(/fransat/)) {
      description = 'Fransat'
      type = 'internet'
      internet = false
    } else if (hostname.match(/nordnet/)) {
      description = 'Nordnet'
      type = 'internet'
      internet = false
    } else if (hostname.match(/numericabletelecom/)) {
      description = 'Numericable Telecom'
      type = 'internet'
      internet = false
    } else if (hostname.match(/ovhtelecom/)) {
      description = 'OVH Telecom'
      type = 'internet'
      internet = false
    } else if (hostname.match(/ozone/)) {
      description = 'Ozone'
      type = 'internet'
      internet = false
    } else if (hostname.match(/canalsat/)) {
      product = 'CANALSAT'
      description = 'Canalsat'
      type = 'canalsat'
    } else if (hostname.match(/canalplay/)) {
      description = 'Canalplay'
      type = 'canalplay'
    } else if (hostname.match(/canalfoot/)) {
      description = 'Foot+/Rugby+'
      type = 'canalfoot'
    } else if (hostname.match(/canal/)) {
      product = 'CANAL_PLUS'
      description = 'Canal+'
      type = 'canal'
    } else if (hostname.match(/mma/)) {
      description = 'MMA'
      type = 'mutuelle'
    } else if (hostname.match(/axa/)) {
      description = 'AXA'
      type = 'mutuelle'
    } else if (hostname.match(/pacifica/)) {
      description = 'Pacifica'
      type = 'mutuelle'
    } else if (hostname.match(/directassurance/)) {
      description = 'Direct Assurance'
      type = 'mutuelle'
    } else if (hostname.match(/maif/)) {
      description = 'MAIF'
      type = 'mutuelle'
    } else if (hostname.match(/macif/)) {
      description = 'Macif'
      type = 'mutuelle'
    } else if (hostname.match(/gmf/)) {
      description = 'GMF'
      type = 'mutuelle'
    } else if (hostname.match(/groupama/)) {
      description = 'Groupama'
      type = 'mutuelle'
    } else if (hostname.match(/matmut/)) {
      description = 'Matmut'
      type = 'mutuelle'
    } else if (hostname.match(/generali/)) {
      description = 'Generali'
      type = 'mutuelle'
    } else if (hostname.match(/basicfit/)) {
      description = 'Basic Fit'
      type = 'sport'
    } else if (hostname.match(/neoness/)) {
      description = 'Neoness'
      type = 'sport'
    } else if (hostname.match(/keepcool/)) {
      description = 'Keep Cool'
      type = 'sport'
    } else if (hostname.match(/fitnesspark/)) {
      description = 'Fitness Park'
      type = 'sport'
    }
    if (type === 'mutuelle') {
      motif = 'Autres raisons'
    }
    return {
      internet,
      stripe: null,
      stripeCX: null,
      loading: false,
      payment: true,
      showCgu: false,
      showRgpd: false,
      showSuccessModal: false,
      products: {
        ORANGE: {
          priceId: 'price_1S4LZoHXlwNyUEJPfOFmVXjd', // price_1S4LZoHXlwNyUEJPfOFmVXjd dev : price_1SidDMHXlwNyUEJPDsLysXbu
          bg: '#000000',
          btn: '#FF7900',
          font: 'inter',
          border: 'rounded'
        },
        SFR: {
          priceId: 'price_1ShZVNHXlwNyUEJPcAz7tNFE', // price_1ShZVNHXlwNyUEJPcAz7tNFE dev : price_1SidE7HXlwNyUEJPmHoV8qHC
          bg: '#000000',
          btn: '#E2001A',
          font: 'inter',
          border: 'rounded'
        },
        FREE_MOBILE: {
          priceId: 'price_1Ss0S1HXlwNyUEJPdK4UOwEd', // price_1Ss0S1HXlwNyUEJPdK4UOwEd dev : price_1SidFrHXlwNyUEJP3IuqY3ow
          bg: '#FFFFFF',
          btn: '#0033A0',
          font: 'inter',
          border: 'rounded'
        },
        FREE: {
          priceId: 'price_1Ss0SAHXlwNyUEJPUglXzob9', // price_1Ss0SAHXlwNyUEJPUglXzob9 dev :  price_1SidGRHXlwNyUEJPygBbRgjz
          bg: '#FFFFFF',
          btn: '#0033A0',
          font: 'inter',
          border: 'rounded'
        },
        BOUYGUES_TELECOM: {
          priceId: 'price_1Ss0SNHXlwNyUEJPYTRbwSuH', // price_1Ss0SNHXlwNyUEJPYTRbwSuH dev : price_1SidHJHXlwNyUEJPeF8yyHKi
          bg: '#FFFFFF',
          btn: '#009FE3',
          font: 'inter',
          border: 'rounded'
        },
        CANAL_PLUS: {
          priceId: 'price_1Ss0SXHXlwNyUEJPVv3kbUtO', // price_1Ss0SXHXlwNyUEJPVv3kbUtO dev : price_1SidIHHXlwNyUEJPcZMsvkDS
          bg: '#FFFFFF',
          btn: '#000000',
          font: 'inter',
          border: 'rounded'
        },
        CANALSAT: {
          priceId: 'price_1Ss0SgHXlwNyUEJPa7EYcMYt', // price_1Ss0SgHXlwNyUEJPa7EYcMYt dev :  price_1SidIqHXlwNyUEJPpa3Zs2ct
          bg: '#FFFFFF',
          btn: '#000000',
          font: 'inter',
          border: 'rounded'
        }
      },
      form: {
        type,
        services,
        motif,
        description,
        product,
        attachment: null,
        informations: null,
        paymentId: null,
        paymentSecret: null,
        phone: null,
        contract: null,
        effectiveDate: 'immediate',
        nom: null,
        prenom: null,
        email: null,
        adresse: null,
        adresse2: null,
        codePostal: null,
        ville: null,
        pays: 'FR',
        optin: '1'
      }
    }
  },
  created () {
    this.preventReload()
    if (this.$route.query.success === '1') {
      this.showSuccessModal = true
      if (!sessionStorage.getItem('conversion_tracked')) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-11163997967/2fCFCJCau5sYEI-utMsp',
          'value': 39.90,
          'currency': 'EUR'
        })
        sessionStorage.setItem('conversion_tracked', '1')
        console.log('+ g_event (checkout)')
      }
      this.$router.replace({ query: {} })
    }
    
    this.$nextTick(async () => {
      this.stripeCX = await loadStripe(this.$root.stripeKey)

      if (this.gclid) {
        // Vue.api.post('/g/' + this.gclid).catch(() => {})
      }
    })
  },
  computed: {
    dailyCount () {
      const hour = new Date().getHours()
      const seed = new Date().toDateString().split('').reduce((a, c) => a + c.charCodeAt(0), 0)
      return 8 + ((seed + hour * 3) % 18)
    },
    keyword () {
      if (this.$route.query.keyword) {
        return this.$route.query.keyword
      }
      let m = window.location.href.match(/keyword=([^&#]+)/)
      if (m) {
        return m[1]
      }
    },
    gclid () {
      if (this.$route.query.gclid) {
        return this.$route.query.gclid
      }
      let m = window.location.href.match(/gclid=([^&#]+)/)
      if (m) {
        return m[1]
      }
    },
    device () {
      if (this.$route.query.device) {
        return this.$route.query.device
      }
      let m = window.location.href.match(/device=([^&#]+)/)
      if (m) {
        return m[1]
      }
    },
    hostname () {
      return window.location.hostname + window.location.pathname
    },
    classname () {
      return this.form.description.replace(/\s+/g, '').toLowerCase()
    },
    contact () {
      let domain = window.location.hostname.split('.')
      return 'contact@' + domain[domain.length - 2] + '.' + domain[domain.length - 1]
    },
    step () {
      if (!this.$route.params.etape) {
        return 0
      }

      let etape = parseInt(this.$route.params.etape)
      return isNaN(etape) ? 0 : etape
    }
  },
  methods: {
    reset () {
      this.$router.push('/')
      window.scrollTo(0, 0)
      window.location.reload(true)
    },
    notifyError (message) {
      this.$notify({
        title: 'Une erreur est survenue',
        message: message || 'Veuillez recharger la page s\'il vous plait.',
        position: 'bottom-right',
        type: 'error',
        duration: 0
      })
    },
    preparePayment () {
      this.form.hostname = this.hostname
      this.form.keyword = this.keyword
      this.form.device = this.device

      if (this.form.product && this.products[this.form.product]) {
        return this.handleCheckout(this.products[this.form.product])
      }

      if (this.payment) {
        window.scrollTo(0, 120)
        Vue.api.post('/payment/intent', this.form).then((res) => {
          this.form.paymentId = res.data.id
          this.form.paymentSecret = res.data.secret
          // window.gtag('event', 'conversion', { 'send_to': 'AW-702902281/vdelCND3s7gBEInglc8C' })
          // console.log('+ g_event')
        }).catch(() => this.notifyError())

        this.$router.push('/etape/1')
        // Vue.api.post('/c/1').catch(() => {})
      }
    },
    handlePayment () {
      this.loading = true
      Vue.api.post('/payment/update', this.form).then(() => {
        handleCardPayment(this.form.paymentSecret, {
          payment_method_data: {
            billing_details: {
              name: this.form.nom + ' ' + this.form.prenom
            }
          },
          save_payment_method: true
        }).then((result) => {
          this.loading = false
          if (result.error) {
            return this.notifyError(result.error.message)
          }

          this.$router.push('/etape/2')
          window.scrollTo(0, 0)
          // cf: composant confirmation window.gtag('event', 'conversion', { 'send_to': 'AW-11163997967/2fCFCJCau5sYEI-utMsp' })
          // console.log('+ g_event')
          // Vue.api.post('/c/2').catch(() => {})
        })
      }).catch(() => this.notifyError())
    },
    handleCheckout (product) {
      this.loading = true

      Vue.api.post('/payment/checkout', {
        ...this.form,
        ...product,
        billing_details: {
          name: this.form.nom + ' ' + this.form.prenom
        }
      }, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }).then((res) => {
        this.loading = false
        return this.stripeCX.redirectToCheckout({ sessionId: res.data.id })
      }).catch(() => this.notifyError())
    },
    contactUs () {
      window.location.href = 'mailto:' + this.contact
    },
    openEspaceClient () {
      window.open(this.espaceClient, '_blank')
    },
    preventReload () {
      setTimeout(() => {
        if (this.step === 1 && !this.form.paymentId) {
          this.reset()
        }
      }, 6000)
    }
  },
  watch: {
    step () {
      this.preventReload()
    }
  }
}
</script>

<style lang="scss" scoped>
  .stripe-checkout-container {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .stripe-checkout-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding:10px 20px;
    min-height: calc(100vh - 40px);
  }

  .stripe-checkout-header {
    width: 100%;
    max-width: 600px;
    margin-top: 24px;
    margin-bottom: 24px;
    text-align: center;
  }

  .stripe-checkout-title {
    font-size: 28px;
    font-weight: 500;
    line-height: 1.2;
    margin: 0;
    color: #0a2540;
    cursor: pointer;
    letter-spacing: -0.02em;
    transition: color 0.2s ease;

    &:hover {
      color: rgb(0, 51, 160);
    }
  }

  .stripe-checkout-price {
    font-size: 16px;
    color: #475569;
    margin: 8px 0 0;
    font-weight: 400;

    strong {
      color: #0a2540;
      font-weight: 600;
    }
  }

  .stripe-checkout-trust {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 12px;
    flex-wrap: wrap;
  }

  .trust-item {
    font-size: 13px;
    color: #64748b;
    white-space: nowrap;
  }

  .trust-separator {
    color: #cbd5e1;
    font-size: 13px;
  }

  .stripe-checkout-social-proof {
    font-size: 13px;
    color: #16a34a;
    margin: 10px 0 0;
    font-weight: 500;
  }

  .stripe-checkout-main {
    width: 100%;
    max-width: 600px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .stripe-checkout-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.04);
    padding: 32px;
    text-align: center;
  }

  .stripe-checkout-steps {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.04);
    padding: 32px;
  }

  .stripe-checkout-footer {
    width: 100%;
    max-width: 600px;
    margin-top: 32px;
    text-align: center;
    padding-top: 24px;
    border-top: 1px solid #e6ebf1;
  }

  .stripe-checkout-link {
    background: none;
    border: none;
    color: #6c757d;
    font-size: 13px;
    cursor: pointer;
    padding: 0;
    margin: 0;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: rgb(0, 51, 160);
    }
  }

  .stripe-checkout-separator {
    color: #d1d5db;
    margin: 0 12px;
    font-size: 13px;
  }

  @media (max-width: 768px) {
    .stripe-checkout-wrapper {
      padding: 24px 16px;
      min-height: calc(100vh - 24px);
    }

    .stripe-checkout-header {
      margin-bottom: 32px;
    }

    .stripe-checkout-title {
      font-size: 24px;
    }

    .stripe-checkout-card {
      padding: 32px 24px;
      border-radius: 8px;
    }

    .stripe-checkout-steps {
      padding: 24px 16px;
      border-radius: 8px;
    }

    .stripe-checkout-footer {
      margin-top: 32px;
      padding-top: 24px;
    }
  }

  @media (max-width: 480px) {
    .stripe-checkout-title {
      font-size: 20px;
    }

    .stripe-checkout-card {
      padding: 24px 16px;
    }

    .stripe-checkout-separator {
      display: block;
      margin: 8px 0;
    }
  }
</style>

<style lang="scss">
  #app, html, body {
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    font-weight: 400;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: #fafbfc;
    color: #0a2540;
    margin: 0;
    padding: 0;
    line-height: 1.5;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 500;
    color: #0a2540;
  }

  img.responsive {
    display: block;
    max-width: 100%;
    height: auto;
  }

  .el-drawer__open .el-drawer.btt {
    overflow: auto;

    .el-card__body {
      font-size: 13px;
    }
  }

  .stripe-checkout-card {
    #form .el-form-item {
      max-width: 600px;
      margin: 0 auto 16px;
      text-align: left;

      &.optin .el-checkbox__input {
        top: -18px;
      }
      &.optin .el-checkbox__label {
        font-weight: 400 !important;
      }
    }

    #cgu {
      background-color: #fafbfc;
      border: 1px solid #e6ebf1;
      border-radius: 6px;
      padding: 16px;
      margin: 24px auto;

      .el-card__body {
        padding: 0;
      }
    }

    h2 {
      font-size: 20px;
      font-weight: 500;
      margin: 0 0 24px 0;
      color: #0a2540;
      text-align: center;
    }

    .el-form {
      text-align: left;
    }

    .choice {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin: 0;

      .el-radio {
        margin: 0 !important;
        width: 100%;
        display: block;

        &.is-bordered {
          border-color: #d1d5db;
        }

        &.is-bordered.is-checked {
          border-color: rgb(0, 51, 160);
        }

        .el-radio__input.is-checked .el-radio__inner {
          border-color: rgb(0, 51, 160);
          background-color: rgb(0, 51, 160);
        }
      }

      .el-checkbox {
        margin: 0 !important;
        width: 100%;
        display: block;
      }
    }

    .el-button.el-button--danger {
      background-color: #dc2626;
      border-color: #dc2626;
      color: #ffffff;
      display: block;
      margin: 0 auto;

      &:hover {
        background-color: #b91c1c;
        border-color: #b91c1c;
      }

      &:active {
        background-color: #991b1b;
        border-color: #991b1b;
      }
    }

    .valider {
      text-align: center;
      margin-top: 24px;

      .el-button {
        margin: 0 auto;
      }
    }
  }

  .pre-select {
    .el-dialog {
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
      max-width: 560px;
      margin: 0 auto;
    }

    .el-dialog__header {
      padding: 32px 32px 24px;
      background: #ffffff;
      border-bottom: none;
      text-align: left;

      .el-dialog__title {
        font-size: 20px;
        font-weight: 600;
        color: #0a2540;
        letter-spacing: -0.01em;
        line-height: 1.4;
      }

      h3 {
        margin-top: 20px;
      }

      .el-dialog__close {
        color: #6c757d;
        font-size: 20px;
        font-weight: 400;

        &:hover {
          color: #0a2540;
        }
      }
    }

    .el-dialog__body {
      padding: 0 32px 24px;
      background: #ffffff;

      /* Cible les conteneurs de choix (choice-logo et le div v-else) */
      > div {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 32px;
      }
    }

    /* Style global pour les radios et checkboxes dans la modale */
    .el-radio, .el-checkbox {
      margin: 0 !important; /* Force le reset des marges Element UI */
      padding: 16px;
      border: 1px solid #e6ebf1;
      border-radius: 8px;
      transition: all 0.2s ease;
      background: #ffffff;
      width: 100%;
      display: flex;
      align-items: center;
      box-sizing: border-box;

      &.is-bordered {
        padding: 12px 20px 10px 10px !important;
        height: auto !important;
        margin-left: 0 !important;
      }

      &:hover {
        border-color: #d1d5db;
        background-color: #fafbfc;
      }

      &.is-checked {
        border-color: rgb(0, 51, 160);
        background-color: rgba(0, 51, 160, 0.05);
      }

      .el-radio__input, .el-checkbox__input {
        display: flex;
        align-items: center;

        .el-radio__inner, .el-checkbox__inner {
          border-color: #d1d5db;
          width: 18px;
          height: 18px;

          &::after {
            width: 8px;
            height: 8px;
            background-color: rgb(0, 51, 160);
          }
        }

        &.is-checked .el-radio__inner, &.is-checked .el-checkbox__inner {
          border-color: rgb(0, 51, 160);
          background-color: #ffffff;
        }
        
        &.is-checked .el-checkbox__inner {
          background-color: rgb(0, 51, 160);
          &::after {
             border-color: #fff;
          }
        }
      }

      .el-radio__label, .el-checkbox__label {
        font-size: 15px;
        color: #0a2540;
        padding-left: 12px;
        font-weight: 400;
        line-height: 1.5;
        white-space: normal; /* Permet le texte sur plusieurs lignes */
        text-align: left;

        img {
          max-height: 24px;
          margin-top: 8px;
          display: block;
        }
      }
    }

    .el-button.el-button--danger {
      width: 100%;
      background-color: rgb(0, 51, 160);
      border-color: rgb(0, 51, 160);
      color: #ffffff;
      border-radius: 6px;
      font-weight: 500;
      font-size: 16px;
      padding: 12px 24px;
      transition: all 0.2s ease;
      margin-top: 8px;

      &:hover {
        background-color: rgb(0, 41, 130);
        border-color: rgb(0, 41, 130);
      }

      &:active {
        background-color: rgb(0, 31, 100);
        border-color: rgb(0, 31, 100);
      }
    }

    .el-checkbox {
      margin: 0 0 12px 0;
      padding: 16px;
      border: 1px solid #e6ebf1;
      border-radius: 8px;
      transition: all 0.2s ease;
      background: #ffffff;
      display: block;
      width: 100%;

      &:hover {
        border-color: #d1d5db;
        background-color: #fafbfc;
      }

        &.is-checked {
          border-color: rgb(0, 51, 160);
          background-color: rgba(0, 51, 160, 0.05);
        }

        .el-checkbox__input {
          .el-checkbox__inner {
            border-color: #d1d5db;
            width: 18px;
            height: 18px;
            border-radius: 4px;

            &::after {
              border-color: rgb(0, 51, 160);
            }
          }

          &.is-checked .el-checkbox__inner {
            border-color: rgb(0, 51, 160);
            background-color: rgb(0, 51, 160);
          }
        }

      .el-checkbox__label {
        font-size: 15px;
        color: #0a2540;
        padding-left: 12px;
        font-weight: 400;
        line-height: 1.5;
      }
    }

    h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: #0a2540;
      letter-spacing: -0.01em;
    }
  }

  /* Modale de succès après paiement */
  .success-modal {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
    max-width: 500px;
    margin: 0 auto;
  }

  .success-modal .el-dialog__header {
    padding: 32px 32px 24px;
    background: #ffffff;
    border-bottom: none;
    text-align: center;
  }

  .success-modal .el-dialog__title {
    font-size: 20px;
    font-weight: 600;
    color: #0a2540;
    letter-spacing: -0.01em;
    line-height: 1.4;
  }

  .success-modal .el-dialog__close {
    color: #6c757d;
    font-size: 20px;
    font-weight: 400;
  }

  .success-modal .el-dialog__close:hover {
    color: #0a2540;
  }

  .success-modal .el-dialog__body {
    padding: 0 32px 32px;
    background: #ffffff;
  }

  .success-modal__body {
    text-align: center;
    padding: 20px 0;
  }

  .success-modal__text {
    font-size: 16px;
    color: #0a2540;
    line-height: 1.6;
    margin: 0;
    word-break: normal;
    hyphens: none;
    overflow-wrap: break-word;
  }

  .success-modal .el-dialog__footer {
    padding: 0 32px 32px;
    text-align: center;
  }

  .success-modal .success-modal-close-btn {
    background-color: #000000 !important;
    border-color: #000000 !important;
    color: #ffffff !important;
    border-radius: 6px;
    font-weight: 500;
    font-size: 16px;
    padding: 12px 24px;
    transition: all 0.2s ease;
  }

  .success-modal .success-modal-close-btn:hover {
    background-color: #333333 !important;
    border-color: #333333 !important;
  }

  .success-modal .success-modal-close-btn:active {
    background-color: #1a1a1a !important;
    border-color: #1a1a1a !important;
  }

  .choice-logo, .el-dialog__body {
    .el-radio + .el-radio {
      margin: 0 !important;
    }
    .el-checkbox + .el-checkbox {
      margin: 0 0 10px 0 !important;
    }
    .el-radio, .el-checkbox {
      padding: 14px;
      height: auto !important;
      width: 100% !important;
    }
  }

  @media (max-width: 768px) {
    /* Modal de succès : pleine largeur avec marges, texte lisible */
    .success-modal.el-dialog {
      margin: 0 16px;
      width: calc(100% - 32px) !important;
      max-width: none;
    }

    .success-modal .el-dialog__header {
      padding: 24px 20px 16px;
    }

    .success-modal .el-dialog__title {
      font-size: 18px;
      line-height: 1.35;
    }

    .success-modal .el-dialog__body {
      padding: 0 20px 24px;
    }

    .success-modal__body {
      padding: 12px 0 20px;
    }

    .success-modal__text {
      font-size: 15px;
      line-height: 1.55;
    }

    .success-modal .el-dialog__footer {
      padding: 0 20px 24px;
    }

    .success-modal .success-modal-close-btn {
      padding: 14px 24px;
      font-size: 16px;
    }

    .stripe-checkout-card {
      #form {
        h2 {
          font-size: 16px;
          margin-bottom: 20px;

          & + .el-form {
            padding-top: 0;
          }
        }

        .el-form--label-left .el-form-item__label {
          width: 100% !important;

          & + div {
            margin-left: 0 !important;
          }
        }

        .el-alert__title {
          display: block;
          padding: 8px 4px;
        }
      }
    }

    main {
      .el-step__title {
        line-height: 16px;
        font-size: 14px;
      }
    }

    .el-step.is-horizontal {
      flex-basis: calc(100vw / 3) !important;
    }

    .pre-select {
      .el-dialog {
        margin: 0 16px;
        width: calc(100% - 32px) !important;
      }

      .el-dialog__header {
        padding: 24px 24px 20px;
      }

      .el-dialog__body {
        padding: 0 24px 24px;
      }

      .choice-logo {
        .el-radio__label, .el-checkbox__label {
          font-size: 12px !important;
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/app.vue